import React, { useState, useMemo, useEffect } from 'react'
import { Dropdown, Ref } from 'semantic-ui-react'
import styled from 'styled-components'
import _ from 'lodash'

/** @typedef {import('semantic-ui-react').StrictDropdownItemProps[]} Options */

/**
 * @typedef FilterMultipleValueInputProps
 * @property {Options} options
 * @property {function} onSearchChange
 * @property {boolean} [searching]
 * @property {boolean} isMultipleValue
 */

/**
 * @template T
 * @param {import('shared/components/FilterGroup/reducer').InputProps<T> & FilterMultipleValueInputProps} props
 */
export default function FilterMultipleValueInput({
  onChange,
  onSearchChange,
  defaultFilters,
  options,
  searching,
  isMultipleValue = true,
}) {
  const defaultValue = defaultFilters.map((filter) => filter.value)
  const [numberOfSelectedValues, setNumberOfSelectedValues] = useState(0)

  function handleChange(_, { value }) {
    // Support single value or multiple values
    const valuesArray = Array.isArray(value) ? value : [value]
    const selectedOptions = valuesArray.map((value) =>
      options.find((option) => option.value === value)
    )
    onChange(
      selectedOptions.map((option) => {
        return {
          value: option.value,
          label: option.text,
        }
      })
    )
    setNumberOfSelectedValues(value.length)
  }

  const debouncedHandleSearchChange = useMemo(() => {
    function handleSearchChange(_, { searchQuery }) {
      if (searchQuery.length >= 1) {
        onSearchChange(searchQuery)
      }
    }

    return _.debounce(handleSearchChange, 500)
  }, [onSearchChange])

  const isSelectedAllOptions = numberOfSelectedValues === options.length

  const message = getMessage(searching, isSelectedAllOptions)

  const [dropdownRef, setDropdownRef] = useState()
  useEffect(() => {
    if (dropdownRef) {
      // Prevent scrolling when focusing on an input element.
      // https://stackoverflow.com/a/48500276
      // https://github.com/simplesat/simplesat/issues/4982#issuecomment-951707931
      dropdownRef.querySelector('input').focus({ preventScroll: true })
    }
  }, [dropdownRef])

  return (
    <CustomDropdownStyleWrapper>
      <Ref innerRef={setDropdownRef}>
        <Dropdown
          options={options}
          search={customSearch}
          selection
          fluid
          multiple={isMultipleValue}
          defaultValue={isMultipleValue ? defaultValue : defaultValue[0]}
          noResultsMessage={message}
          onSearchChange={debouncedHandleSearchChange}
          onChange={handleChange}
        />
      </Ref>
    </CustomDropdownStyleWrapper>
  )
}

function getMessage(searching, isSelectedAllOptions) {
  if (searching) {
    return 'Searching...'
  }

  if (isSelectedAllOptions) {
    return 'Start typing to search more'
  }

  return 'No result found.'
}

const CustomDropdownStyleWrapper = styled.div`
  .ui.dropdown .menu > .item {
    transform: scaleY(-1);

    > span {
      display: block;
      transform: scaleY(-1);
      margin: 0;
      float: none;
    }

    > .description,
    .text {
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
    }
  }
`

function customSearch(options, query) {
  return options.filter((option) => {
    const textToSearch = option.text + (option.description || '')
    return textToSearch.toLowerCase().indexOf(query.toLowerCase()) >= 0
  })
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/components/FilterMultipleValueInput/index.js